import React from "react";
import {
  Avatar,
  Box,
  Button,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography
} from "@mui/material";
import { AccessTime } from "@mui/icons-material";
import {
  Dashboard as DashboardIcon,
  PersonAdd as PersonAddIcon,
  People as PeopleIcon,
  ContentPasteSearch as ContentPasteSearch,
  SupportAgent as SupportAgentIcon
} from "@mui/icons-material";
import { BASE_URL } from "../services/customerApi";

const getAvatarSrc = (picStr) => {
  if (!picStr) return "";
  if (picStr.startsWith("http")) return picStr;
  if (picStr.startsWith("avatar_")) {
    try {
      return require(`../assets/avatars/${picStr}`);
    } catch (e) {
      return "";
    }
  }
  return `${BASE_URL}${picStr}`;
};


export default function Sidebar({
  drawerOpen,
  toggleDrawer,
  activeSection,
  setActiveSection,
  handleOpenRegistration,
  onLogout,
  userRole
}) {
  const allMenuItems = [
    { label: "User Creation", section: "user-creation", icon: <PersonAddIcon />, adminOnly: true },
    { label: "User Details", section: "user-details", icon: <PeopleIcon />, adminOnly: true },
    { label: "Dashboard", section: "dashboard", icon: <DashboardIcon /> },
    { label: "Customer Registration", section: "registration", icon: <PersonAddIcon /> },
    { label: "Customer Enquiry", section: "users", icon: <ContentPasteSearch /> },
    { label: "Lead Module", section: "LeadModule", icon: <SupportAgentIcon  /> },
    // { label: "Profile", section: "profile", icon: <VerifiedIcon /> },

  ];

  const menuItems = allMenuItems.filter(item => {
    if (userRole?.toUpperCase() === "ADMIN") return true;
    if (userRole?.toUpperCase() === "MARKETER") return item.section === "users";
    return !item.adminOnly;
  });

  // ✅ READ login time (display only)
  const loginTime = sessionStorage.getItem("lastLoginTime");
  const loginDate = loginTime ? new Date(loginTime) : null;

  const profilePic = sessionStorage.getItem("profilePic");
  const userName = sessionStorage.getItem("userName") || "User";
  const isAdmin = userRole?.toUpperCase() === "ADMIN";

  const displayName = userName;
  const displayRole = userRole || "Employee";
  const displayPic = getAvatarSrc(profilePic);

  return (
    <Drawer
      variant="temporary"
      open={drawerOpen}
      onClose={toggleDrawer}
      sx={{
        "& .MuiDrawer-paper": {
          width: 285,
          background: "linear-gradient(180deg, #111111 0%, #0a0a0a 100%)",
          color: "white",
          borderRight: "1px solid rgba(253, 184, 19, 0.2)",
          boxShadow: "10px 0 30px rgba(0,0,0,0.5)"
        }
      }}
    >
      <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>

        {/* HEADER - Clickable to Profile ONLY for non-admins */}
        <Box
          onClick={() => {
            if (!isAdmin) {
              setActiveSection(userRole === "MARKETER" ? "users" : "profile");
              toggleDrawer();
            }
          }}
          sx={{
            p: 3,
            borderBottom: "1px solid rgba(255,255,255,.25)",
            cursor: isAdmin ? "default" : "pointer",
            transition: "all 0.3s ease",
            "&:hover": {
              bgcolor: isAdmin ? "transparent" : "rgba(253, 184, 19, 0.05)"
            }
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
              alignItems: "center"
            }}
          >
            <Avatar
              sx={{
                bgcolor: "#FDB813",
                color: "#111111",
                width: 80,
                height: 80,
                border: "3px solid rgba(253, 184, 19, 0.3)",
                fontSize: "2rem",
                fontWeight: 800
              }}
              alt="Profile Picture"
              src={displayPic}
            >
              {!displayPic && displayName.charAt(0).toUpperCase()}
            </Avatar>
            <Typography
              fontWeight={700}
              sx={{
                fontFamily: "'Poppins', sans-serif",
                fontSize: "1.1rem"
              }}
            >
              {displayName}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                fontFamily: "'Poppins', sans-serif",
                color: "#FDB813",
                fontWeight: 600,
                mt: -1,
                opacity: 0.8
              }}
            >
              {displayRole}
            </Typography>
          </Box>

          {/* ✅ LOGIN TIME DISPLAY */}
          {loginDate && (
            <Box
              sx={{
                mt: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 0.5,
                background: "linear-gradient(135deg, rgba(255,255,255,0.1), rgba(255,255,255,0.05))",
                border: "1px solid rgba(255,255,255,0.15)",
                p: 1,
                borderRadius: 2,
                backdropFilter: "blur(10px)"
              }}
            >
              <AccessTime
                fontSize="small"
                sx={{
                  fontSize: 12,
                  opacity: 0.7,
                  fontFamily: "'Poppins', sans-serif"
                }}
              />
              <Typography
                variant="caption"
                sx={{
                  fontSize: 10,
                  fontWeight: 500,
                  letterSpacing: 0.3,
                  fontFamily: "'Poppins', sans-serif"
                }}
              >
                {loginDate.toLocaleDateString("en-IN", {
                  day: "numeric",
                  month: "short",
                  year: "numeric"
                })}
                {" • "}
                {loginDate.toLocaleTimeString("en-IN", {
                  hour: "2-digit",
                  minute: "2-digit"
                })}
              </Typography>
            </Box>
          )}
        </Box>

        {/* MENU */}
        <Box sx={{ p: 2 }}>
          <List>
            {menuItems.map(m => (
              <ListItemButton
                key={m.section}
                onClick={() => {
                  if (m.section === "registration") {
                    handleOpenRegistration();
                  } else {
                    setActiveSection(m.section);
                  }
                  toggleDrawer();
                }}
                sx={{
                  borderRadius: 2,
                  mb: 0.8,
                  background:
                    activeSection === m.section
                      ? "rgba(253, 184, 19, 0.15)"
                      : "transparent",
                  borderLeft:
                    activeSection === m.section
                      ? "6px solid #FDB813"
                      : "6px solid transparent",
                  transition: "all 0.25s ease",
                  "&:hover": {
                    background: "rgba(253, 184, 19, 0.1)",
                    transform: "translateX(8px)"
                  }
                }}
              >
                <ListItemIcon sx={{ color: activeSection === m.section ? "#FDB813" : "#ffffff", minWidth: 40 }}>
                  {m.icon}
                </ListItemIcon>
                <ListItemText
                  primary={m.label}
                  primaryTypographyProps={{
                    sx: {
                      fontFamily: "'Poppins', sans-serif",
                      fontWeight: 500,
                      fontSize: "0.95rem"
                    }
                  }}
                />
              </ListItemButton>
            ))}
          </List>
        </Box>

        {/* FOOTER */}
        <Box
          sx={{
            mt: "auto",
            p: 2,
            borderTop: "1px solid rgba(255,255,255,.25)"
          }}
        >
          <Button
            fullWidth
            variant="outlined"
            onClick={() => {
              toggleDrawer();
              onLogout();
            }}
            sx={{
              color: "#ffffff",
              borderColor: "rgba(253, 184, 19, 0.5)",
              borderRadius: 2,
              fontWeight: 800,
              fontFamily: "'Outfit', sans-serif",
              textTransform: "none",
              fontSize: "0.95rem",
              py: 1,
              "&:hover": {
                background: "rgba(253, 184, 19, 0.1)",
                borderColor: "#FDB813"
              }
            }}
          >
            Logout
          </Button>
        </Box>

      </Box>
    </Drawer>
  );
}